import React from 'react';
import PropTypes from 'prop-types';

import { PowerFunnelSurfaces } from '../../Analytics/constants';
import useIsMobile from '../../hooks/useIsMobile';
import useGlobalPrivilegesCta from '../../hooks/useGlobalPrivilegesCta';
import styles from './hotelPlannerCard.scss';

const HotelPlannerBanner = ({ surface = PowerFunnelSurfaces.HOME, variant }) => {
    const { isMobileWidth } = useIsMobile();
    const {
        isPowered,
        hasGlobalPrivilegesAccess,
        hasPastDueSubscription,
        handleCtaClick,
        handleLearnMoreClick,
        buttonText,
        pastDueSubscriptionMessage,
    } = useGlobalPrivilegesCta({ surface, variant, trackImpressions: true });

    const getTagline = () => {
        if (hasGlobalPrivilegesAccess) {
            return 'See how Power now pays for itself!';
        }
        if (isPowered) {
            return 'Upgrade today and unlock over $250 of value in travel benefits:';
        }
        return 'Unlock Power, the most valuable and affordable tennis membership.';
    };

    return (
        <div className={styles.hotelPlannerBanner}>
            <div className={styles.bannerInner}>
                <div className={styles.imageSection}>
                    <img
                        src="/img/v3/graphics/perks/hotel-planner.jpg"
                        alt="Tennis court"
                        className={styles.bannerImage}
                    />
                </div>
                <div className={styles.contentSection}>
                    {isMobileWidth ? (
                        <>
                            <p className={styles.headline} data-testid="banners.hotel-planner.mobile-headline">
                            Update to a Power annual plan and unlock:
                            </p>
                            <ul className={styles.benefitsList}>
                                <li>Over $250 of value in travel benefits</li>
                                <li>Up to 60% off on 1.4 million hotels globally</li>
                            </ul>
                        </>
                    ) : (
                        <>
                            <p className={styles.headline} data-testid="banners.hotel-planner.desktop-headline">
                                {hasGlobalPrivilegesAccess || isPowered
                                    ? 'The Power annual membership now pays for itself with two memberships in one.'
                                    : 'Sign up for our Power annual membership and unlock over $250 of value in travel benefits: '}
                            </p>
                            {hasGlobalPrivilegesAccess && (
                                <p className={styles.benefitsHeadline} data-testid="banners.hotel-planner.benefits-headline">
                                    Power now gives you access to over $250 of value in travel benefits:
                                </p>
                            )}
                            <div className={styles.benefitsContainer}>
                                <ul className={styles.benefitsList}>
                                    <li>Save up to 60% on 1.4 million hotels globally</li>
                                    <li>$100 excursion credit or $100 hotel voucher</li>
                                    <li>Airport lounge pass + lounge discounts</li>
                                </ul>
                                <ul className={styles.benefitsList}>
                                    <li>Discounted car rentals</li>
                                    <li>Discounted event tickets</li>
                                    <li>Discount on cruises</li>
                                </ul>
                            </div>
                            <p className={`${styles.tagline} ${isPowered ? styles.taglineBold : ''}`} data-testid="banners.hotel-planner.tagline">
                                {getTagline()}
                            </p>
                        </>
                    )}
                    {hasPastDueSubscription && <p className={styles.pastDueSubscriptionMessage} data-testid="banners.hotel-planner.past-due-message">{pastDueSubscriptionMessage}</p>}
                    <button
                        className={`btn btn-primary text-uppercase ${styles.ctaButton}`}
                        onClick={handleCtaClick}
                        type="button"
                        data-testid="banners.hotel-planner.cta-btn"
                    >
                        {buttonText}
                    </button>
                    {!hasGlobalPrivilegesAccess && (
                        <div
                            className={styles.learnMore}
                            onClick={handleLearnMoreClick}
                            data-testid="banners.hotel-planner.learn-more"
                        >
                            <span className={styles.underline} data-testid="banners.hotel-planner.learn-more-link">Learn more</span> about Global Privileges
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

HotelPlannerBanner.propTypes = {
    surface: PropTypes.oneOf([PowerFunnelSurfaces.HOME, PowerFunnelSurfaces.POWER_PERKS_PAGE]),
    variant: PropTypes.string,
};

HotelPlannerBanner.defaultProps = {
    surface: PowerFunnelSurfaces.HOME,
    variant: null,
};

export default HotelPlannerBanner;
